import { OneDataSource } from "./DataSource";
import { Schema } from "./Schema";
import { action, observable, runInAction } from "mobx";
import {
  Api,
  CommonError,
  Logger,
  LoggerFactory,
  RemoteOneDataSourceOptions
} from "../index";
import { RemoteExecutor } from "./RemoteExecutor";
import { isEqual } from "lodash";
export class RemoteOneDataSource<T, ID> extends RemoteExecutor
  implements OneDataSource<T, ID> {
  public schema: Schema;
  @observable public one?: T;
  @observable public id?: ID;
  public url: string;

  public persisted?: T;
  public api: Api;
  public log: Logger;

  public constructor({
    api,
    name,
    schema,
    url,
    commons
  }: RemoteOneDataSourceOptions) {
    super(name, commons);
    this.schema = schema;
    this.log = LoggerFactory.create(`RemoteOneDataSource [${this.name}]`);
    this.url = url;
    this.api = api;
    this.commons = commons!;
  }

  @action
  public async findOne(id: ID): Promise<T> {
    this.log.debug("findOne with id", id);
    this.id = id;
    return await this.executeRemote("one.load", async () => {
      const response = await this.api.getOne<T>(
        this.url + this.schema.link(id)
      );

      return this.findOneSuccess(response);
    });
  }

  @action
  protected findOneSuccess(response: T): T {
    this.one = response;
    this.persisted = this.one;
    return response;
  }

  @action
  public async save(): Promise<T> {
    let response: T;

    response = await this.executeRemote("save", async () => {
      if (!this.one) {
        throw { message: "saving undefined object" } as CommonError;
      }

      const resp = await this.api.save<T>(this.url, this.one);

      if (resp) {
        this.commons.newSuccess("Created");
      }

      return resp;
    });

    runInAction(() => {
      this.one = response;
      this.persisted = this.one;
    });

    return response;
  }

  @action
  public async update(): Promise<T> {
    let response: T;

    response = await this.executeRemote("update", async () => {
      if (!this.one) {
        throw { message: "updating undefined object" } as CommonError;
      }

      const resp = await this.api.update<T>(
        this.url + this.schema.link(this.one),
        this.one
      );

      if (resp) {
        this.commons.newSuccess("Updated");
      }

      return resp;
    });

    runInAction(() => {
      this.one = response;
    });

    return this.one as T;
  }

  @action
  public async partialUpdate(dataPart: {}): Promise<void> {
    await this.executeRemote("update", async () => {
      if (!this.one) {
        throw { message: "updating undefined object" } as CommonError;
      }

      const resp = await this.api.partialUpdate<T>(
        this.url + this.schema.link(this.one),
        dataPart
      );
      this.triggerEvent("update.success");

      if (resp) {
        this.commons.newSuccess("Updated");
      }
    });
  }

  @action
  public async clone(): Promise<T> {
    const response = await this.executeRemote("save", async () => {
      if (this.id === undefined) {
        throw { message: "Cloning with undefined id" } as CommonError;
      }

      const resp = (await this.api.postMethod(
        this.url + this.schema.link(this.id) + "/copy",
        this.one
      )) as T;

      if (resp) {
        this.commons.newSuccess("Created");
      }

      return resp;
    });

    runInAction(() => {
      this.one = response;
      this.persisted = this.one;
    });

    return response;
  }

  @action
  public async delete(): Promise<void> {
    await this.executeRemote("delete", async () => {
      if (!this.one) {
        throw { message: "deleting undefined object" } as CommonError;
      }

      await this.api.delete(this.url + this.schema.link(this.one));
    });

    runInAction(() => {
      this.clear();
    });
  }

  @action
  public clearPkFields(): T | undefined {
    if (this.one) {
      const diff = this.one;
      this.schema.primaryKey.forEach((key: string) => {
        delete diff[key];
      });
      this.one = diff;
    }
    return this.one;
  }

  @action
  public clear(): void {
    this.id = undefined;
    this.one = undefined;
    this.persisted = undefined;
    this.triggerEvent("one.clear");
  }

  @action
  public link(): string | null {
    return this.id ? this.url + this.schema.link(this.id) : null;
  }

  @action
  public async download(type: string): Promise<void> {
    await this.executeRemote("download", async () => {
      await this.api.fileDownload(
        this.url + this.schema.link(this.one as {}),
        undefined,
        { headers: { Accept: type } }
      );
    });
  }

  @action
  public async upload(): Promise<void> {
    await this.executeRemote("upload", async () => {
      await this.api.fileUpload(
        this.url + this.schema.link(this.one as {}),
        this.one
      );
    });
  }

  @action
  public change(one: T): T {
    if (!isEqual(this.one, one)) {
      this.one = one;
      this.triggerEvent("one.changed", this.one);
    }

    return this.one as T;
  }

  @action
  public rollback(): T | undefined {
    this.one = this.persisted;
    return this.one;
  }
}
